import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { FieldTree } from '@angular/forms/signals';
import { SvgIconComponent } from 'angular-svg-icon';

@Component({
  selector: 'app-radio-input',
  imports: [SvgIconComponent],
  templateUrl: './radio-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // A caller's static `id` attribute also lands on this host element, and this keeps
  // it off, since `optionInputId`/`labelId` already derive their own ids from it.
  host: { '[attr.id]': 'null' },
})
export class RadioInputComponent {
  readonly field = input.required<FieldTree<string | null>>();
  readonly id = input('');
  readonly label = input('');
  readonly hideLabel = input(false);
  readonly options = input<string[]>([]);
  readonly containerClass = input('');

  protected readonly state = computed(() => this.field()());

  get labelId(): string {
    return `${this.id()}-label`;
  }

  protected readonly labelText = computed(() =>
    this.state().required() ? `${this.label()} *` : this.label()
  );

  get hasSelection(): boolean {
    const value = this.state().value();
    return value !== null && value !== undefined;
  }

  get showClearButton(): boolean {
    return this.hasSelection;
  }

  get canClear(): boolean {
    return this.showClearButton && !this.state().disabled();
  }

  get optionList(): string[] {
    return this.options();
  }

  isSelected(option: string): boolean {
    return this.state().value() === option;
  }

  // Native [class] object bindings drop any key containing a space, unlike
  // NgClass, so each Tailwind class needs its own key here.
  indicatorClasses(option: string): Record<string, boolean> {
    const disabled = this.state().disabled();
    const selected = this.isSelected(option);
    const invalidTouched =
      this.state().touched() && this.state().errors().length > 0;
    const disabledUnselected = disabled && !selected;
    const invalidAndTouched = !disabled && invalidTouched;
    const selectedValid = selected && (disabled || this.state().valid());
    const unselectedOrInvalid = !disabled && (!selected || invalidTouched);
    return {
      'bg-input-disabled-subtle': disabledUnselected,
      'border-disabled': disabledUnselected,
      'text-quarternary': disabledUnselected,
      'cursor-not-allowed': disabledUnselected,
      'border-error': invalidAndTouched,
      'text-error-primary': invalidAndTouched,
      'border-brand-solid': selectedValid,
      'bg-brand-solid': selectedValid,
      'border-primary': unselectedOrInvalid,
      'bg-primary': invalidAndTouched || unselectedOrInvalid,
    };
  }

  onToggle(option: string, event: Event): void {
    if (this.state().disabled()) {
      return;
    }

    const input = event.target as HTMLInputElement | null;
    const checked = input?.checked ?? false;

    this.state().value.set(checked ? option : null);
    this.state().markAsDirty();
    this.state().markAsTouched();
  }

  clearSelection(event: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.canClear) return;
    this.state().value.set(null);
    this.state().markAsDirty();
    this.state().markAsTouched();
  }

  trackByOption(_: number, option: string): string {
    return option;
  }

  optionInputId(index: number): string {
    return `${this.id()}-option-${index}`;
  }
}
